import { createContext, type ReactNode, useContext, useEffect } from 'react';
import * as stylex from '@stylexjs/stylex';
import { tenantBrand } from '../theme/semantic.stylex';
import { type Branding, DEFAULT_BRANDING } from './types';
import { validateBrandColor } from './validate-color';

// BrandingProvider drives tenant-customizable accents.
//
// Mechanism:
//   1. The `tenantBrand` StyleX theme variant rebases the `brandPrimary`
//      sentinel token to read from a runtime CSS variable
//      (`--sentinel-brand-primary`) with the VicOne purple as fallback.
//   2. This provider sets that CSS variable on <html> from the active
//      `branding.primaryColor` and applies the theme class to its root
//      so descendant components that consume `sentinel.brandPrimary`
//      pick up the tenant color automatically.
//
// Components should reference `sentinel.brandPrimary` (from semantic.stylex)
// for any tenant-skinnable accent. Non-skinnable elements (system surfaces,
// risk-tier indicators, action links) should use miraidon `colors.*`
// directly.

const BrandingContext = createContext<Branding>(DEFAULT_BRANDING);

export interface BrandingProviderProps {
  branding?: Branding;
  children: ReactNode;
}

export function BrandingProvider({ branding = DEFAULT_BRANDING, children }: BrandingProviderProps) {
  useEffect(() => {
    const root = document.documentElement;
    // Security boundary: tenant input flows into a CSS custom property that is
    // interpolated unquoted into a color-mix() expression downstream. The
    // validator is the only line of defense — do not bypass it.
    const safe = validateBrandColor(branding.primaryColor) ?? DEFAULT_BRANDING.primaryColor;
    root.style.setProperty('--sentinel-brand-primary', safe);
    return () => {
      root.style.removeProperty('--sentinel-brand-primary');
    };
  }, [branding.primaryColor]);
  return (
    <BrandingContext.Provider value={branding}>
      <div {...stylex.props(tenantBrand)}>{children}</div>
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
